import React, { useState, useEffect, useCallback } from 'react';
import { Tab } from '@components/atoms/Tab';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { MaterialsSubTab } from './MaterialsSubTab';
import { EquipmentSubTab } from './EquipmentSubTab';
import { AddWarehouseMaterialModal } from '@components/organisms/AddWarehouseMaterialModal';
import { ReturnMaterialModal } from '@components/organisms/ReturnMaterialModal';
import { TaskEquipmentCheckoutModal } from '@components/organisms/TaskEquipmentCheckoutModal';
import { TaskEquipmentReserveModal } from '@components/organisms/TaskEquipmentReserveModal';
import { EquipmentReturnModal } from '@components/organisms/EquipmentReturnModal';
import { CalculatorMatchingModal } from '@components/organisms/CalculatorMatchingModal';
import ConstructionCalculatorsModal from '@components/calculators/ConstructionCalculatorsModal';
import { MaterialFromCalculator, CalculatorType } from '@components/calculators/types/calculator.types';
import {
  useTaskMaterials,
  useTaskMaterialSummary,
  useUpdateTaskMaterial,
  useDeleteTaskMaterial,
  useReturnTaskMaterial,
  useReserveTaskMaterial,
  useConsumeTaskMaterial,
  useTaskEquipments,
  useCheckoutTaskEquipment,
  useReturnTaskEquipment,
  useDeleteTaskEquipment,
  useStockLevels,
} from '@hooks/api/warehouse';
import {
  TaskMaterial,
  TaskMaterialUpdateInput,
  TaskMaterialStatus,
  MaterialSource,
  TaskEquipment,
  TaskEquipmentStatus,
  TaskEquipmentReturnInput,
} from '@models/warehouse.model';
import { Material } from '@models/material.model';
import { Equipment } from '@models/equipment.model';
import { useMaterials } from '@hooks/api/material/useMaterials';
import { useCreateMaterial } from '@hooks/api/material/useCreateMaterial';
import { useEditMaterial } from '@hooks/api/material/useEditMaterial';
import { useDeleteMaterial } from '@hooks/api/material/useDeleteMaterial';
import { useEquipment } from '@hooks/api/Equipment/useEquipment';
import { useCreateEquipment } from '@hooks/api/Equipment/useCreateEquipment';
import { useEditEquipment } from '@hooks/api/Equipment/useEditEquipment';
import { useDeleteEquipment } from '@hooks/api/Equipment/useDeleteEquipment';
import { CreateEditMaterialModal } from './CreateEditMaterialModal';
import { CreateEditEquipmentModal } from './CreateEditEquipmentModal';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useTranslation } from '@hooks/useTranslation';

interface InventoryTabProps {
  taskId?: string;
  isDelegated?: boolean;
  taskStartDate?: string;
  taskEndDate?: string;
  initialSubTab?: string;
}

export const InventoryTab: React.FC<InventoryTabProps> = ({
  taskId,
  isDelegated = false,
  taskStartDate,
  taskEndDate,
  initialSubTab,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<string>(initialSubTab || 'materials');

  // Modal states - Warehouse
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [showMatchingModal, setShowMatchingModal] = useState(false);
  const [showReturnMaterialModal, setShowReturnMaterialModal] = useState(false);
  const [showDeleteMaterialModal, setShowDeleteMaterialModal] = useState(false);
  const [showCheckoutEquipmentModal, setShowCheckoutEquipmentModal] = useState(false);
  const [showReserveEquipmentModal, setShowReserveEquipmentModal] = useState(false);
  const [showReturnEquipmentModal, setShowReturnEquipmentModal] = useState(false);
  const [showDeleteEquipmentModal, setShowDeleteEquipmentModal] = useState(false);
  const [showCheckoutReservedModal, setShowCheckoutReservedModal] = useState(false);

  // Modal states - Manual
  const [showManualMaterialModal, setShowManualMaterialModal] = useState(false);
  const [showManualEquipmentModal, setShowManualEquipmentModal] = useState(false);
  const [showDeleteManualMaterialModal, setShowDeleteManualMaterialModal] = useState(false);
  const [showDeleteManualEquipmentModal, setShowDeleteManualEquipmentModal] = useState(false);

  // Selected items - Warehouse
  const [selectedMaterial, setSelectedMaterial] = useState<TaskMaterial | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<TaskEquipment | null>(null);
  const [calculatorResults, setCalculatorResults] = useState<MaterialFromCalculator[]>([]);
  const [calculatorType, setCalculatorType] = useState<CalculatorType | undefined>(undefined);

  // Selected items - Manual (for edit/delete)
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [deletingManualMaterial, setDeletingManualMaterial] = useState<Material | null>(null);
  const [deletingManualEquipment, setDeletingManualEquipment] = useState<Equipment | null>(null);

  // Data hooks - Warehouse
  const { data: taskMaterials = [], isLoading: materialsLoading } = useTaskMaterials(taskId);
  const {
    data: materialSummary,
    isLoading: materialSummaryLoading,
    isError: materialSummaryError,
    refetch: refetchMaterialSummary,
  } = useTaskMaterialSummary(taskId);
  const { data: taskEquipment = [], isLoading: equipmentLoading } = useTaskEquipments(taskId);

  // Fetch all stock levels for badge display (cached 2min by React Query)
  const { data: stockLevels = [] } = useStockLevels();

  // Data hooks - Manual
  const { materials: manualMaterials, fetchMaterials, loading: manualMaterialsLoading } = useMaterials();
  const { equipments: manualEquipment, fetchEquipments, loading: manualEquipmentLoading } = useEquipment();

  // Mutation hooks - Manual
  const { createMaterial: createManualMaterial } = useCreateMaterial();
  const { editMaterial: editManualMaterial } = useEditMaterial();
  const { deleteMaterial: deleteManualMaterial } = useDeleteMaterial();
  const { createEquipment: createManualEquipment } = useCreateEquipment();
  const { editEquipment: editManualEquipment } = useEditEquipment();
  const { deleteEquipment: deleteManualEquipment } = useDeleteEquipment();

  // Mutations - Warehouse
  const updateMaterial = useUpdateTaskMaterial();
  const deleteMaterial = useDeleteTaskMaterial();
  // Called for its hook slot only; the mutation is not triggered from here.
  useReturnTaskMaterial();
  const reserveTaskMaterial = useReserveTaskMaterial();
  const consumeTaskMaterial = useConsumeTaskMaterial();
  const checkoutTaskEquipment = useCheckoutTaskEquipment();
  const returnTaskEquipment = useReturnTaskEquipment();
  const deleteTaskEquipment = useDeleteTaskEquipment();

  // Fetch manual materials/equipment when taskId changes
  const refreshManualData = useCallback(() => {
    if (taskId) {
      fetchMaterials(taskId);
      fetchEquipments(taskId);
    }
  }, [taskId, fetchMaterials, fetchEquipments]);

  useEffect(() => {
    refreshManualData();
  }, [refreshManualData]);

  // --- New Task: Save first message ---
  if (!taskId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
          <WarehouseIcon className="text-indigo-600" sx={{ fontSize: 32 }} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {tTaskDetail.inventoryTab.saveTaskFirstTitle}
        </h3>
        <p className="text-sm text-gray-500 text-center max-w-sm">
          {tTaskDetail.inventoryTab.saveTaskFirstMessage}
        </p>
      </div>
    );
  }

  // --- Handlers ---
  const handleCalculatorResults = (materials: MaterialFromCalculator[], type: CalculatorType) => {
    setCalculatorResults(materials);
    setCalculatorType(type);
    setShowCalculatorModal(false);
    setShowMatchingModal(true);
  };

  const handleDeleteMaterial = () => {
    if (!selectedMaterial) return;

    const onSuccess = () => {
      setShowDeleteMaterialModal(false);
      setSelectedMaterial(null);
    };

    deleteMaterial.mutate(selectedMaterial.taskMaterialId, { onSuccess });
  };

  const handleUpdateMaterial = (input: TaskMaterialUpdateInput) => {
    updateMaterial.mutate(input);
  };

  const handleReturnEquipment = async (input: TaskEquipmentReturnInput) => {
    await returnTaskEquipment.mutateAsync(input);
    setShowReturnEquipmentModal(false);
    setSelectedEquipment(null);
  };

  const handleDeleteEquipment = () => {
    if (!selectedEquipment) return;
    deleteTaskEquipment.mutate(selectedEquipment.taskEquipmentId, {
      onSuccess: () => {
        setShowDeleteEquipmentModal(false);
        setSelectedEquipment(null);
      },
    });
  };

  const handleCheckoutReserved = () => {
    if (!selectedEquipment) return;
    checkoutTaskEquipment.mutate(selectedEquipment.taskEquipmentId, {
      onSuccess: () => {
        setShowCheckoutReservedModal(false);
        setSelectedEquipment(null);
      },
    });
  };

  // --- Manual Material Handlers ---
  const handleSaveManualMaterial = async (materialData: any) => {
    if (!taskId) return;
    const params = {
      ...materialData,
      taskId,
    };

    if (editingMaterial?.materialId && !editingMaterial.materialId.startsWith('temp-')) {
      await editManualMaterial({ ...params, materialId: editingMaterial.materialId });
    } else {
      await createManualMaterial(params);
    }

    setShowManualMaterialModal(false);
    setEditingMaterial(null);
    refreshManualData();
  };

  const handleEditManualMaterial = (material: Material) => {
    setEditingMaterial(material);
    setShowManualMaterialModal(true);
  };

  const handleDeleteManualMaterial = async () => {
    if (!deletingManualMaterial?.materialId) return;
    const success = await deleteManualMaterial(deletingManualMaterial.materialId);
    if (success) {
      setShowDeleteManualMaterialModal(false);
      setDeletingManualMaterial(null);
      refreshManualData();
    }
  };

  // --- Manual Equipment Handlers ---
  const handleSaveManualEquipment = async (equipmentData: any) => {
    if (!taskId) return;
    const params = {
      ...equipmentData,
      taskId,
    };

    if (editingEquipment?.equipmentId && !editingEquipment.equipmentId.startsWith('temp-')) {
      await editManualEquipment({ ...params, equipmentId: editingEquipment.equipmentId });
    } else {
      await createManualEquipment(params);
    }

    setShowManualEquipmentModal(false);
    setEditingEquipment(null);
    refreshManualData();
  };

  const handleEditManualEquipment = (equipment: Equipment) => {
    setEditingEquipment(equipment);
    setShowManualEquipmentModal(true);
  };

  const handleDeleteManualEquipment = async () => {
    if (!deletingManualEquipment?.equipmentId) return;
    const success = await deleteManualEquipment(deletingManualEquipment.equipmentId);
    if (success) {
      setShowDeleteManualEquipmentModal(false);
      setDeletingManualEquipment(null);
      refreshManualData();
    }
  };

  return (
    <>
      {/* Sub-Tabs */}
      <Tab
        tabs={[
          {
            id: 'materials',
            name: tTaskDetail.inventoryTab.materialsTabLabel,
            count: taskMaterials.length + manualMaterials.length,
          },
          {
            id: 'equipment',
            name: tTaskDetail.inventoryTab.equipmentTabLabel,
            count: taskEquipment.length + manualEquipment.length,
          },
        ]}
        activeTab={activeSubTab}
        onTabChange={(id) => setActiveSubTab(id)}
        variant="pills"
        size="sm"
      />

      {/* Sub-Tab Content */}
      <div className="mt-4">
        {activeSubTab === 'materials' && (
          <MaterialsSubTab
            taskMaterials={taskMaterials}
            materialSummary={materialSummary}
            materialSummaryLoading={materialSummaryLoading}
            materialSummaryError={materialSummaryError}
            onRetrySummary={() => refetchMaterialSummary()}
            stockLevels={stockLevels}
            isLoading={materialsLoading || manualMaterialsLoading}
            isDelegated={isDelegated}
            taskStartDate={taskStartDate}
            onAddFromWarehouse={() => setShowAddMaterialModal(true)}
            onOpenCalculator={() => setShowCalculatorModal(true)}
            onReturnMaterial={(material) => {
              setSelectedMaterial(material);
              setShowReturnMaterialModal(true);
            }}
            onDeleteMaterial={(material) => {
              setSelectedMaterial(material);
              setShowDeleteMaterialModal(true);
            }}
            onUpdateMaterial={handleUpdateMaterial}
            onReserveMaterial={(material) => {
              reserveTaskMaterial.mutate(material.taskMaterialId);
            }}
            onConsumeMaterial={(material) => {
              consumeTaskMaterial.mutate(material.taskMaterialId);
            }}
            manualMaterials={manualMaterials}
            onAddManually={() => {
              setEditingMaterial(null);
              setShowManualMaterialModal(true);
            }}
            onEditManual={handleEditManualMaterial}
            onDeleteManual={(material) => {
              setDeletingManualMaterial(material);
              setShowDeleteManualMaterialModal(true);
            }}
          />
        )}
        {activeSubTab === 'equipment' && (
          <EquipmentSubTab
            taskEquipment={taskEquipment}
            isLoading={equipmentLoading || manualEquipmentLoading}
            isDelegated={isDelegated}
            onCheckoutEquipment={() => setShowCheckoutEquipmentModal(true)}
            onReserveEquipment={() => setShowReserveEquipmentModal(true)}
            onReturnEquipment={(equipment) => {
              setSelectedEquipment(equipment);
              setShowReturnEquipmentModal(true);
            }}
            onCheckoutReserved={(equipment) => {
              setSelectedEquipment(equipment);
              setShowCheckoutReservedModal(true);
            }}
            onDeleteEquipment={(equipment) => {
              setSelectedEquipment(equipment);
              setShowDeleteEquipmentModal(true);
            }}
            manualEquipment={manualEquipment}
            onAddManually={() => {
              setEditingEquipment(null);
              setShowManualEquipmentModal(true);
            }}
            onEditManual={handleEditManualEquipment}
            onDeleteManual={(equipment) => {
              setDeletingManualEquipment(equipment);
              setShowDeleteManualEquipmentModal(true);
            }}
          />
        )}
      </div>

      {/* ===== MODALS ===== */}

      {/* Add Material from Warehouse */}
      <AddWarehouseMaterialModal
        isOpen={showAddMaterialModal}
        onClose={() => setShowAddMaterialModal(false)}
        taskId={taskId}
        isDelegated={isDelegated}
      />

      {/* Calculator */}
      <ConstructionCalculatorsModal
        isOpen={showCalculatorModal}
        onClose={() => setShowCalculatorModal(false)}
        onAddAllMaterials={handleCalculatorResults}
      />

      {/* Calculator Matching */}
      <CalculatorMatchingModal
        isOpen={showMatchingModal}
        onClose={() => {
          setShowMatchingModal(false);
          setCalculatorResults([]);
          setCalculatorType(undefined);
          refreshManualData();
        }}
        calculatorResults={calculatorResults}
        calculatorType={calculatorType}
        taskId={taskId}
      />

      {/* Return Material */}
      <ReturnMaterialModal
        isOpen={showReturnMaterialModal}
        onClose={() => {
          setShowReturnMaterialModal(false);
          setSelectedMaterial(null);
        }}
        taskMaterial={selectedMaterial}
      />

      {/* Delete Material Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteMaterialModal}
        onClose={() => {
          setShowDeleteMaterialModal(false);
          setSelectedMaterial(null);
        }}
        onConfirm={handleDeleteMaterial}
        title={tTaskDetail.inventoryTab.removeMaterialTitle}
        description={`${tTaskDetail.inventoryTab.removeMaterialConfirm.replace('{name}', selectedMaterial?.name || '')} ${
          selectedMaterial?.status === TaskMaterialStatus.RESERVED
            ? tTaskDetail.inventoryTab.reservedMaterialReleaseNote
            : selectedMaterial?.status === TaskMaterialStatus.PLANNED
              ? tTaskDetail.inventoryTab.plannedMaterialNoStockNote
              : selectedMaterial?.source === MaterialSource.WAREHOUSE
                ? tTaskDetail.inventoryTab.warehouseStockReturnedNote
                : ''
        }`}
        confirmText={tTaskDetail.inventoryTab.remove}
        cancelText={tTaskDetail.inventoryTab.cancel}
        variant="danger"
        size="small"
      />

      {/* Checkout Equipment */}
      <TaskEquipmentCheckoutModal
        isOpen={showCheckoutEquipmentModal}
        onClose={() => setShowCheckoutEquipmentModal(false)}
        taskId={taskId}
        isDelegated={isDelegated}
      />

      {/* Reserve Equipment */}
      <TaskEquipmentReserveModal
        isOpen={showReserveEquipmentModal}
        onClose={() => setShowReserveEquipmentModal(false)}
        taskId={taskId}
        taskStartDate={taskStartDate}
        taskEndDate={taskEndDate}
        isDelegated={isDelegated}
      />

      {/* Delete Equipment Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteEquipmentModal}
        onClose={() => {
          setShowDeleteEquipmentModal(false);
          setSelectedEquipment(null);
        }}
        onConfirm={handleDeleteEquipment}
        title={tTaskDetail.inventoryTab.removeEquipmentTitle}
        description={`${tTaskDetail.inventoryTab.removeEquipmentConfirm.replace('{name}', selectedEquipment?.name || '')} ${
          selectedEquipment?.status === TaskEquipmentStatus.RESERVED
            ? tTaskDetail.inventoryTab.reservedEquipmentCancelNote
            : selectedEquipment?.status === TaskEquipmentStatus.PLANNED
              ? tTaskDetail.inventoryTab.plannedEquipmentNoAssetNote
              : ''
        }`}
        confirmText={tTaskDetail.inventoryTab.remove}
        cancelText={tTaskDetail.inventoryTab.cancel}
        variant="danger"
        size="small"
      />

      {/* Checkout Reserved Equipment Confirmation */}
      <ConfirmationModal
        isOpen={showCheckoutReservedModal}
        onClose={() => {
          setShowCheckoutReservedModal(false);
          setSelectedEquipment(null);
        }}
        onConfirm={handleCheckoutReserved}
        title={tTaskDetail.inventoryTab.checkoutEquipmentTitle}
        description={tTaskDetail.inventoryTab.checkoutEquipmentConfirm.replace(
          '{name}',
          selectedEquipment?.name || '',
        )}
        confirmText={tTaskDetail.inventoryTab.checkout}
        cancelText={tTaskDetail.inventoryTab.cancel}
        variant="info"
        size="small"
      />

      {/* Return Equipment */}
      <EquipmentReturnModal
        isOpen={showReturnEquipmentModal}
        onClose={() => {
          setShowReturnEquipmentModal(false);
          setSelectedEquipment(null);
        }}
        onReturn={handleReturnEquipment}
        equipment={selectedEquipment}
        isLoading={returnTaskEquipment.isPending}
        isDelegated={isDelegated}
      />

      {/* ===== MANUAL MODALS ===== */}

      {/* Add/Edit Manual Material */}
      <CreateEditMaterialModal
        isOpen={showManualMaterialModal}
        onClose={() => {
          setShowManualMaterialModal(false);
          setEditingMaterial(null);
        }}
        onSave={handleSaveManualMaterial}
        material={editingMaterial}
        isDelegated={isDelegated}
      />

      {/* Add/Edit Manual Equipment */}
      <CreateEditEquipmentModal
        isOpen={showManualEquipmentModal}
        onClose={() => {
          setShowManualEquipmentModal(false);
          setEditingEquipment(null);
        }}
        onSave={handleSaveManualEquipment}
        equipment={editingEquipment}
        isDelegated={isDelegated}
      />

      {/* Delete Manual Material Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteManualMaterialModal}
        onClose={() => {
          setShowDeleteManualMaterialModal(false);
          setDeletingManualMaterial(null);
        }}
        onConfirm={handleDeleteManualMaterial}
        title={tTaskDetail.inventoryTab.removeMaterialTitle}
        description={tTaskDetail.inventoryTab.removeMaterialConfirm.replace(
          '{name}',
          deletingManualMaterial?.name || '',
        )}
        confirmText={tTaskDetail.inventoryTab.remove}
        cancelText={tTaskDetail.inventoryTab.cancel}
        variant="danger"
        size="small"
      />

      {/* Delete Manual Equipment Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteManualEquipmentModal}
        onClose={() => {
          setShowDeleteManualEquipmentModal(false);
          setDeletingManualEquipment(null);
        }}
        onConfirm={handleDeleteManualEquipment}
        title={tTaskDetail.inventoryTab.removeEquipmentTitle}
        description={tTaskDetail.inventoryTab.removeEquipmentConfirm.replace(
          '{name}',
          deletingManualEquipment?.name || '',
        )}
        confirmText={tTaskDetail.inventoryTab.remove}
        cancelText={tTaskDetail.inventoryTab.cancel}
        variant="danger"
        size="small"
      />
    </>
  );
};
